import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Plus, TrendingUp, Eye, MousePointerClick, DollarSign, ShieldCheck, ArrowRight, Sparkles, Handshake, X, Wallet, Wand2, Store as StoreIcon, Rocket } from "lucide-react";
import { toast } from "sonner";
import ReadinessWidget from "@/components/merchant/ReadinessWidget";
import WelcomeTour from "@/components/merchant/WelcomeTour";
import BusinessTypeBanner from "@/components/merchant/BusinessTypeBanner";
import ReservedPlanBanner from "@/components/merchant/ReservedPlanBanner";
import PlanBenefitsBanner from "@/components/merchant/PlanBenefitsBanner";
import { VisitsUpsellBanner } from "@/components/merchant/VisitsUpsellBanner";
import { usePricing, cad } from "@/lib/pricing";

export default function MerchantOverview() {
  const [store, setStore] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [boost, setBoost] = useState(null);
  const [prompt, setPrompt] = useState(null);
  const [promptBusy, setPromptBusy] = useState(false);
  const [netRevenue, setNetRevenue] = useState(null);
  const finalPricing = !!usePricing()?.final_active;
  const [aiDrafts, setAiDrafts] = useState([]);
  const [storefrontCount, setStorefrontCount] = useState(null);
  const [firstStorefrontReady, setFirstStorefrontReady] = useState(null);
  const [walletSurface, setWalletSurface] = useState(null);
  const [recapSettings, setRecapSettings] = useState(null);
  const [recapSaving, setRecapSaving] = useState(false);

  useEffect(() => {
    api.get("/merchant/store").then((r) => setStore(r.data));
    api.get("/merchant/analytics").then((r) => setAnalytics(r.data));
    api.get("/merchant/coupons").then((r) => setCoupons(r.data));
    api.get("/merchant/verify-boost-stats").then((r) => setBoost(r.data));
    api.get("/merchant/analytics/wallet-surface?days=30")
      .then((r) => setWalletSurface(r.data))
      .catch(() => setWalletSurface(null));
    api.get("/merchant/cashback-recap/preview")
      .then((r) => setRecapSettings(r.data?.settings || null))
      .catch(() => setRecapSettings(null));
    api.get("/merchant/reseller-prompt")
      .then((r) => setPrompt(r.data?.show ? r.data : null))
      .catch(() => setPrompt(null));
    api.get("/merchant/billing/net-revenue")
      .then((r) => setNetRevenue(r.data))
      .catch(() => setNetRevenue(null));
    api.get("/ai-coupons/drafts?limit=3")
      .then((r) => setAiDrafts(r.data.items || []))
      .catch(() => setAiDrafts([]));
    // MERCHANT-ONBOARDING-REVIEW-1 · Phase 2 — surface the onboarding
    // guide for merchants with 0 or incomplete storefronts.
    api.get("/v1/merchant/storefronts")
      .then(async (r) => {
        const list = r.data?.storefronts || [];
        setStorefrontCount(list.length);
        if (list.length) {
          try {
            const rd = await api.get(
              `/v1/merchant/storefront/readiness?sid=${list[0].id}`,
            );
            setFirstStorefrontReady(!!rd.data?.ready);
          } catch { setFirstStorefrontReady(null); }
        }
      })
      .catch(() => setStorefrontCount(null));
  }, []);

  const toggleRecapEmail = async (value) => {
    setRecapSaving(true);
    try {
      const r = await api.patch("/merchant/cashback-recap/settings", {
        cashback_recap_email: value,
      });
      setRecapSettings(r.data.settings);
    } catch {
      // silent — banner keeps prior state on failure
    } finally {
      setRecapSaving(false);
    }
  };

  async function discardDraft(sid) {
    if (!window.confirm("Discard this AI draft? This can't be undone.")) return;
    try {
      await api.post(`/ai-coupons/${sid}/cancel`, { reason: "discarded_from_overview" });
      setAiDrafts((d) => d.filter((x) => x.id !== sid));
      toast.success("Draft discarded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to discard draft");
    }
  }

  async function decidePrompt(action) {
    if (!prompt) return;
    setPromptBusy(true);
    try {
      const body = { action };
      if (action === "opt_in") body.campaign_id = prompt.best_campaign?.id;
      await api.post("/merchant/reseller-prompt/decision", body);
      if (action === "opt_in") {
        toast.success(`"${prompt.best_campaign?.name}" is now live in the reseller catalog.`);
      } else {
        toast.info("You can still enable resellers manually from any campaign row.");
      }
      setPrompt(null);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Failed to apply your choice"));
    } finally { setPromptBusy(false); }
  }

  const stats = [
    { icon: MousePointerClick, label: "Total clicks", value: analytics?.total_clicks ?? "—" },
    { icon: Eye, label: "Total views", value: analytics?.total_views ?? "—" },
    { icon: TrendingUp, label: "CTR", value: analytics ? `${analytics.ctr}%` : "—" },
    { icon: DollarSign, label: "Revenue", value: analytics ? cad(analytics.revenue ?? 0) : "—" },
  ];

  const showBoostCTA = boost && !boost.current_verified;

  return (
    <div data-testid="merchant-overview">
      <WelcomeTour />
      <ReservedPlanBanner />
      <PlanBenefitsBanner />
      <BusinessTypeBanner />
      <VisitsUpsellBanner />
      {/* MERCHANT-ONBOARDING-REVIEW-1 Phase 2 · First-Storefront onboarding.
          For merchants with 0 storefronts we replace the empty dashboard
          with a prominent "Create your first Storefront" card. For those
          with an INCOMPLETE first storefront we show a lighter "Continue
          Setup" card. Existing storefronts + ready → no card. */}
      {storefrontCount === 0 && (
        <div className="pf-card mb-6 overflow-hidden relative border-cyan-200/80 bg-gradient-to-br from-cyan-50 via-white to-emerald-50"
             data-testid="onboarding-first-storefront-card">
          <div className="absolute -top-16 -right-16 w-64 h-64 bg-cyan-200/40 rounded-full blur-3xl" />
          <div className="absolute -bottom-16 -left-16 w-56 h-56 bg-emerald-200/40 rounded-full blur-3xl" />
          <div className="relative p-6 md:p-8 grid md:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-cyan-700">
                <StoreIcon className="w-4 h-4" /> Get started
              </div>
              <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-slate-900 mt-2">
                Create your first <span className="pf-gradient-text-2 font-semibold">Storefront</span>
              </h3>
              <p className="text-sm text-slate-600 mt-3 max-w-xl leading-relaxed">
                Set up your business presence, select your Province Coverage, configure your shopper destination and prepare your Storefront for Offers.
              </p>
              <div className="mt-4">
                <Link to="/merchant/storefront-setup"
                      className="pf-btn-primary px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2"
                      data-testid="onboarding-start-cta">
                  <Rocket className="w-4 h-4" /> Start Storefront Setup
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
      {storefrontCount > 0 && firstStorefrontReady === false && (
        <div className="pf-card mb-6 overflow-hidden relative border-amber-200/80 bg-amber-50/50"
             data-testid="onboarding-continue-setup-card">
          <div className="relative p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-700">Storefront in progress</div>
              <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight text-slate-900 mt-1">
                Continue Storefront Setup
              </h3>
              <p className="text-xs md:text-sm text-slate-600 mt-2">
                Complete a few remaining items so you can publish your first Offer.
              </p>
            </div>
            <Link to="/merchant/storefront-setup"
                  className="pf-btn-primary px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1.5"
                  data-testid="onboarding-continue-cta">
              Continue <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      )}

      {/* Loop MWEB-2 · Readiness banner + card (backend is source of truth) */}
      <ReadinessWidget />

      {walletSurface && (walletSurface.totals.wallet_views > 0 || walletSurface.totals.receipt_views > 0) && (
        <div className="pf-card mb-6" data-testid="cashback-loop-overview-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Cashback loop · last 30 days</div>
              <h3 className="font-display text-lg font-medium tracking-tight text-slate-900 mt-1">
                Shoppers are engaging with your rewards
              </h3>
            </div>
            <a href="/merchant/tracking" className="text-xs font-semibold text-cyan-600 hover:underline">View details →</a>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-3 rounded-xl bg-white/60" data-testid="overview-wallet-views">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Wallet views</div>
              <div className="font-mono text-2xl mt-1">{walletSurface.totals.wallet_views}</div>
            </div>
            <div className="p-3 rounded-xl bg-white/60" data-testid="overview-receipt-views">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Receipt views</div>
              <div className="font-mono text-2xl mt-1">{walletSurface.totals.receipt_views}</div>
            </div>
            <div className="p-3 rounded-xl bg-white/60" data-testid="overview-view-merchant">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Store visits from receipts</div>
              <div className="font-mono text-2xl mt-1">{walletSurface.totals.view_merchant_taps}</div>
            </div>
            <div className="p-3 rounded-xl bg-white/60" data-testid="overview-report-issue">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Issue reports</div>
              <div className="font-mono text-2xl mt-1">{walletSurface.totals.report_issue_taps}</div>
            </div>
          </div>
          {recapSettings && (
            <div className="mt-4 pt-4 border-t border-white/60 flex items-center justify-between gap-3" data-testid="overview-recap-banner">
              <div>
                <div className="text-sm font-semibold text-slate-800">Daily cashback recap email</div>
                <div className="text-xs text-slate-500">A summary of the last 24 hours lands in your inbox every day at 13:00 UTC.</div>
              </div>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={recapSettings.cashback_recap_email !== false}
                  disabled={recapSaving}
                  onChange={(e) => toggleRecapEmail(e.target.checked)}
                  data-testid="overview-recap-toggle"
                  className="w-4 h-4 rounded"
                />
                <span className="text-xs font-semibold text-slate-700">
                  {recapSettings.cashback_recap_email !== false ? "On" : "Off"}
                </span>
              </label>
            </div>
          )}
        </div>
      )}

      {prompt && prompt.best_campaign && (
        <div className="pf-card mb-6 overflow-hidden relative border-emerald-200/80 bg-gradient-to-br from-emerald-50 via-white to-cyan-50"
             data-testid="reseller-nudge-banner">
          <div className="absolute -top-16 -right-16 w-64 h-64 bg-emerald-200/40 rounded-full blur-3xl" />
          <div className="absolute -bottom-16 -left-16 w-56 h-56 bg-cyan-200/40 rounded-full blur-3xl" />
          <button
            onClick={() => decidePrompt("dismiss")}
            disabled={promptBusy}
            className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/60 text-slate-500 disabled:opacity-40 z-10"
            data-testid="reseller-nudge-dismiss"
            title="Dismiss — you can still opt in manually later"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="relative p-6 md:p-8 grid md:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <div className="flex items-center gap-2">
                <Handshake className="w-5 h-5 text-emerald-700" />
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700">
                  {prompt.conversions.toLocaleString()} conversions — nice work
                </div>
              </div>
              <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-slate-900 mt-2">
                Let approved resellers push your best campaign
                <span className="pf-gradient-text-2 font-semibold"> "{prompt.best_campaign.name}"</span>
              </h3>
              <p className="text-sm text-slate-600 mt-3 max-w-xl leading-relaxed">
                You've crossed {prompt.threshold}+ verified conversions. Cyannect's approved reseller network can pick this offer up and put it in front of their audiences — you only pay them when they drive a sale.
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <div className="glass rounded-xl px-3 py-2">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Best-performing offer</div>
                  <div className="text-sm text-slate-900 font-medium mt-0.5" data-testid="reseller-nudge-campaign-name">
                    {prompt.best_campaign.name}
                    {prompt.best_campaign.discount_value != null && (
                      <span className="text-emerald-700 font-semibold ml-2">
                        · {prompt.best_campaign.discount_type === "percent"
                            ? `${prompt.best_campaign.discount_value}% off`
                            : `CA$${prompt.best_campaign.discount_value} off`}
                      </span>
                    )}
                  </div>
                </div>
                {prompt.best_campaign.conversions > 0 && (
                  <div className="glass rounded-xl px-3 py-2">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Track record</div>
                    <div className="text-sm text-slate-900 font-medium mt-0.5">
                      {prompt.best_campaign.conversions.toLocaleString()} conversions · {cad(prompt.best_campaign.gmv)} GMV
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex md:flex-col items-stretch gap-2 md:min-w-[220px]">
              <button
                onClick={() => decidePrompt("opt_in")}
                disabled={promptBusy}
                className="pf-btn-primary px-6 py-3 rounded-full font-semibold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-40"
                data-testid="reseller-nudge-optin"
              >
                {promptBusy ? "Applying…" : (<>Enable resellers <ArrowRight className="w-4 h-4" /></>)}
              </button>
              <button
                onClick={() => decidePrompt("dismiss")}
                disabled={promptBusy}
                className="pf-btn-glass px-6 py-2 rounded-full text-xs font-semibold text-slate-600 disabled:opacity-40"
                data-testid="reseller-nudge-later"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      )}

      {showBoostCTA && (
        <div
          className="pf-card mb-6 overflow-hidden relative border-cyan-200/80 bg-gradient-to-br from-cyan-50 via-white to-emerald-50"
          data-testid="verify-boost-cta"
        >
          <div className="absolute -top-16 -right-16 w-64 h-64 pf-gradient-bg rounded-full blur-3xl opacity-40" />
          <div className="absolute -bottom-16 -left-16 w-56 h-56 bg-gradient-to-br from-emerald-200 to-cyan-200 rounded-full blur-3xl opacity-40" />
          <div className="relative p-6 md:p-8 grid md:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-cyan-700" />
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-cyan-700">Verify to boost</div>
              </div>
              <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-slate-900 mt-2">
                Verified brands see{" "}
                <span className="pf-gradient-text-2 font-semibold">+{boost.uplift_pct}% more clicks</span>
                {" "}on Cyannect
                {boost.computed_from_data ? " (measured live)" : " (industry average)"}.
              </h3>
              <p className="text-sm text-slate-600 mt-3 max-w-xl leading-relaxed">
                Upload a business number and one document to unlock the verified badge on every coupon card. Shoppers trust verified brands, and our data shows it turns into real clicks.
              </p>
              <div className="mt-4 grid grid-cols-3 gap-3 max-w-md">
                <div className="glass rounded-xl p-3">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Verified brands</div>
                  <div className="font-display text-xl text-slate-900 tabular-nums mt-1">{boost.verified_avg_clicks} <span className="text-xs text-slate-500 font-normal">clicks/coupon</span></div>
                </div>
                <div className="glass rounded-xl p-3">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Unverified</div>
                  <div className="font-display text-xl text-slate-900 tabular-nums mt-1">{boost.unverified_avg_clicks} <span className="text-xs text-slate-500 font-normal">clicks/coupon</span></div>
                </div>
                <div className="glass rounded-xl p-3 border-cyan-200/80">
                  <div className="text-[10px] uppercase tracking-widest text-cyan-700 font-semibold">Your uplift</div>
                  <div className="font-display text-xl text-cyan-700 tabular-nums mt-1 flex items-center gap-1">
                    <Sparkles className="w-3.5 h-3.5" /> {boost.projected_extra_clicks_7d ? `+${boost.projected_extra_clicks_7d}` : "—"}
                    <span className="text-xs text-slate-500 font-normal">{boost.projected_extra_clicks_7d ? "clicks est." : "not enough data yet"}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex md:flex-col items-center gap-2">
              <Link to="/merchant/verification" className="pf-btn-primary px-6 py-3 rounded-full font-semibold text-sm inline-flex items-center gap-2 whitespace-nowrap" data-testid="boost-verify-cta">
                Start verification <ArrowRight className="w-4 h-4" />
              </Link>
              <div className="text-[10px] text-slate-500 hidden md:block">Takes ~2 minutes</div>
            </div>
          </div>
        </div>
      )}

      {store && store.status !== "approved" && (
        <div className="pf-card p-4 mb-6 flex items-center justify-between bg-[color:var(--pf-mint)]/20 border-[color:var(--pf-mint)]">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold">Pending approval</div>
            <div className="text-sm mt-1">Your store profile is awaiting admin approval. Once approved, your coupons become visible in the marketplace.</div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-3xl font-semibold tracking-tight">Overview</h2>
          <p className="text-sm text-muted-foreground mt-1">A quick snapshot of your Cyannect performance.</p>
        </div>
        <Link to="/merchant/coupons" className="pf-btn-primary px-4 py-2 rounded-md font-semibold text-sm inline-flex items-center gap-2" data-testid="merchant-new-coupon">
          <Plus className="w-4 h-4" /> New coupon
        </Link>
      </div>

      {netRevenue && netRevenue.month && (
        <div className="pf-card p-6 mb-6 bg-gradient-to-br from-emerald-50 via-white to-cyan-50 border border-emerald-100 relative overflow-hidden"
             data-testid="merchant-net-revenue">
          <div className="absolute -top-12 -right-12 w-48 h-48 bg-emerald-200/30 rounded-full blur-3xl" />
          <div className="relative grid md:grid-cols-4 gap-4">
            <div>
              <div className="inline-flex items-center gap-1.5 text-emerald-700 text-[10px] uppercase tracking-widest font-semibold">
                <Wallet className="w-3.5 h-3.5" /> Net revenue · {netRevenue.month}
              </div>
              <div className="font-display text-3xl text-slate-900 tabular-nums mt-1" data-testid="net-revenue-value">
                {cad(netRevenue.net || 0)}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">after Cyannect performance fees</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Gross this month</div>
              <div className="font-display text-2xl text-slate-900 tabular-nums mt-1">
                {cad(netRevenue.gross || 0)}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">{netRevenue.conversions} verified conversions</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Fees paid</div>
              <div className="font-display text-2xl text-rose-600 tabular-nums mt-1">
                −{cad(netRevenue.fees || 0)}
              </div>
              <div className="text-xs text-slate-500 mt-0.5" data-testid="net-revenue-fee-note">{finalPricing ? "Your plan's fee per verified sale · no per-sale cap" : "5.99% Free / 3.99% Std / 3.49% Pro · capped CA$49.99 / CA$30 / CA$30"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Fee ratio</div>
              <div className="font-display text-2xl text-slate-900 tabular-nums mt-1" data-testid="net-revenue-ratio">
                {netRevenue.gross > 0 ? `${((netRevenue.fees / netRevenue.gross) * 100).toFixed(2)}%` : "—"}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {netRevenue.capped_transactions > 0
                  ? `${netRevenue.capped_transactions} transaction${netRevenue.capped_transactions === 1 ? "" : "s"} hit the cap`
                  : "of gross this month"}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((s, i) => (
          <div key={i} className="pf-card p-5">
            <s.icon className="w-4 h-4 text-muted-foreground" />
            <div className="font-display text-3xl font-semibold mt-3">{s.value}</div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {aiDrafts.length > 0 && (
        <div className="mt-10" data-testid="ai-drafts-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-2xl font-semibold tracking-tight flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-cyan-600" />
              AI drafts · pick up where you left off
            </h3>
            <Link to="/merchant/coupons/ai" className="text-xs text-cyan-700 hover:underline flex items-center gap-1"
                  data-testid="ai-drafts-new-link">
              Start a new one <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {aiDrafts.map((d) => {
              const disc = d.draft?.discount_type === "percent"
                ? `${d.draft.discount_value}% OFF`
                : d.draft?.discount_type === "fixed"
                  ? `CA$${d.draft.discount_value} OFF`
                  : (d.draft?.discount_type || "Draft").toUpperCase();
              const updated = d.updated_at
                ? new Date(d.updated_at).toLocaleDateString("en-CA", { month: "short", day: "numeric" })
                : "";
              const scope = (d.draft?.product_ids || []).length > 0
                ? `${d.draft.product_ids.length} product${d.draft.product_ids.length === 1 ? "" : "s"}`
                : d.draft?.category_id ? "Category" : null;
              return (
                <div key={d.id} className="pf-card p-5 flex flex-col relative group" data-testid={`ai-draft-card-${d.id}`}>
                  <button onClick={() => discardDraft(d.id)}
                          className="absolute top-3 right-3 p-1 rounded hover:bg-rose-100 text-slate-400 hover:text-rose-600 opacity-0 group-hover:opacity-100 transition"
                          title="Discard draft"
                          data-testid={`ai-draft-discard-${d.id}`}>
                    <X className="w-4 h-4" />
                  </button>
                  <div className="pf-chip pf-chip-cyan text-[10px] self-start">{disc}</div>
                  <div className="font-semibold text-slate-900 mt-2 line-clamp-2" data-testid={`ai-draft-title-${d.id}`}>
                    {d.draft?.title || "Untitled draft"}
                  </div>
                  <div className="text-xs text-slate-500 mt-1 line-clamp-2">
                    {d.last_user_message || d.draft?.description || "No description yet"}
                  </div>
                  <div className="flex items-center gap-2 mt-3 text-[11px] text-slate-500">
                    {scope && <span className="pf-chip pf-chip-lav text-[10px]">{scope}</span>}
                    <span>Updated {updated}</span>
                    <span>· {d.message_count} msg{d.message_count === 1 ? "" : "s"}</span>
                  </div>
                  <Link to={`/merchant/coupons/ai?sid=${d.id}`}
                        className="mt-auto pt-4 text-sm font-semibold text-cyan-700 hover:text-cyan-900 inline-flex items-center gap-1"
                        data-testid={`ai-draft-continue-${d.id}`}>
                    Continue <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-10">
        <h3 className="font-display text-2xl font-semibold tracking-tight mb-4">Top performing coupons</h3>
        <div className="pf-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-5 py-3">Coupon</th>
                <th className="px-5 py-3">Clicks</th>
                <th className="px-5 py-3">Views</th>
              </tr>
            </thead>
            <tbody>
              {(analytics?.top_coupons || []).map((c, i) => (
                <tr key={i} className="border-t border-border">
                  <td className="px-5 py-3 font-medium">{c.title}</td>
                  <td className="px-5 py-3">{c.clicks}</td>
                  <td className="px-5 py-3">{c.views}</td>
                </tr>
              ))}
              {(!analytics?.top_coupons || analytics.top_coupons.length === 0) && (
                <tr><td colSpan={3} className="px-5 py-8 text-center text-muted-foreground">No coupon data yet. Create your first coupon.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-10">
        <h3 className="font-display text-2xl font-semibold tracking-tight mb-4">Your coupons ({coupons.length})</h3>
        <div className="grid md:grid-cols-2 gap-4">
          {coupons.slice(0, 4).map((c) => (
            <div key={c.id} className="pf-card p-4">
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-medium">{c.title}</div>
                  <div className="text-xs text-muted-foreground mt-1">{c.code || "Auto discount"}</div>
                </div>
                <span className={`pf-chip ${c.status === "approved" ? "bg-[color:var(--pf-mint)] text-black" : "bg-secondary text-foreground"}`}>{c.status}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
